import { Stack } from "@mui/material";
import { ExceptionBar } from "~/components/ExceptionBar/ExceptionBar";
import { useFetchExceptions } from "~/components/exceptions/use-fetch-exceptions";
import { ExceptionStatus, ExceptionType } from "~/operations";
import { ExceptionGroup } from "~/pages/findings/Exceptions/types";

export type OpenExceptionsProps = {
  scopeMrn: string;
  exceptionId?: string;
  types: ExceptionType[];
  refetchQueries?: (() => void)[];
  exceptionsGroups?: ExceptionGroup[];
};

export function OpenExceptions({
  scopeMrn,
  exceptionId,
  types,
  refetchQueries,
  exceptionsGroups,
}: OpenExceptionsProps) {
  const {
    exceptionGroups: fetchedExceptionGroups,
    refetch: refetchExceptions,
    extensions,
  } = useFetchExceptions({
    scopeMrn,
    types,
    exceptionId,
    includeChildScopes: true,
    includeExtension: true,
    skip: !exceptionId || types.length === 0,
  });

  const sortedExceptionGroups = (
    exceptionsGroups && exceptionsGroups.length > 0
      ? exceptionsGroups
      : fetchedExceptionGroups || []
  )
    // filter out exception groups that are rejected, expired or removed
    .filter(
      (group) =>
        group.status === ExceptionStatus.New ||
        group.status === ExceptionStatus.Approved ||
        group.status === ExceptionStatus.NeedsExtension,
    )
    .sort((a, b) => {
      // sort by created at descending
      return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    });

  if (sortedExceptionGroups.length === 0) {
    return null;
  }

  const refetchRequiredData = () => {
    refetchExceptions();
    if (refetchQueries) {
      refetchQueries.forEach((refetchQuery) => {
        refetchQuery?.();
      });
    }
  };

  return (
    <Stack direction="column" gap={1} sx={{ mt: 2 }}>
      {sortedExceptionGroups.map((exceptionGroup) => (
        <ExceptionBar
          key={exceptionGroup.id}
          {...{
            exceptionGroup,
            refetch: refetchRequiredData,
            extensions,
          }}
        />
      ))}
    </Stack>
  );
}
